import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Receipt, LayoutDashboard, Pill, FileSpreadsheet, FileText, Users, BarChart3,
  Settings as SettingsIcon, LogOut, Menu, X, Building2, PlusCircle, PackagePlus,
  Truck, ShoppingBag, BadgePercent, CalendarClock, UserCog, Globe, BookUser,
  ClipboardList, Stethoscope, CalendarCheck, Store, UserCheck, RotateCcw, FolderTree, BellRing, Star, BookOpen, MessagesSquare
} from "lucide-react";

const sections = [
  {
    title: "Counter",
    items: [
      { name: "POS Billing", to: "/pos", icon: Receipt, highlight: true, roles: ["admin", "manager", "agent"] },
      { name: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
      { name: "Online Orders", to: "/online-orders", icon: Globe, badge: true, roles: ["admin", "manager", "agent"] },
      { name: "Reviews", to: "/reviews", icon: Star, roles: ["admin", "manager"] },
      { name: "Blog", to: "/blog-manager", icon: BookOpen, roles: ["admin", "manager"] },
      { name: "Chat Logs", to: "/chat-logs", icon: MessagesSquare, roles: ["admin", "manager"] },
    ],
  },
  {
    title: "Inventory",
    managerOnly: true,
    items: [
      { name: "Add Medicine", to: "/add-medicine", icon: PlusCircle, roles: ["admin", "manager"] },
      { name: "Add Stock", to: "/add-stock", icon: PackagePlus, roles: ["admin", "manager"] },
      { name: "Medicines", to: "/medicines", icon: Pill, roles: ["admin", "manager"] },
      { name: "Categories", to: "/categories", icon: FolderTree, roles: ["admin", "manager"] },
      { name: "Bulk Import", to: "/import", icon: FileSpreadsheet, roles: ["admin", "manager"] },
      { name: "Suppliers", to: "/suppliers", icon: Truck, roles: ["admin", "manager"] },
      { name: "Purchase Orders", to: "/purchase-orders", icon: ClipboardList, roles: ["admin", "manager"] },
      { name: "Expire Soon", to: "/expire-soon", icon: CalendarClock, roles: ["admin", "manager"] },
      { name: "Offers", to: "/offers", icon: BadgePercent, roles: ["admin", "manager"] },
    ],
  },
  {
    title: "Sales",
    items: [
      { name: "Invoices", to: "/invoices", icon: FileText, roles: ["admin", "manager", "agent"] },
      { name: "Customers", to: "/customers", icon: Users, roles: ["admin", "manager", "agent"] },
      { name: "Credit Ledger", to: "/credits", icon: BookUser, roles: ["admin", "manager"] },
      { name: "Returns & Refunds", to: "/returns", icon: RotateCcw, roles: ["admin", "manager"] },
      { name: "Sale Report", to: "/sale-report", icon: ShoppingBag, roles: ["admin", "manager"] },
      { name: "Profit & Loss", to: "/profit-loss", icon: BadgePercent, roles: ["admin", "manager"] },
      { name: "Daily Closing", to: "/closing", icon: CalendarCheck, roles: ["admin", "manager"] },
      { name: "Doctors", to: "/doctors", icon: Stethoscope, roles: ["admin", "manager"] },
    ],
  },
  {
    title: "Admin",
    items: [
      { name: "Reports", to: "/reports", icon: BarChart3, roles: ["admin", "manager"] },
      { name: "CA Report", to: "/ca-report", icon: FileText, roles: ["admin", "manager", "ca"] },
      { name: "Attendance & Salary", to: "/attendance", icon: UserCheck, roles: ["admin", "manager", "hr"] },
      { name: "Branches", to: "/branches", icon: Store, roles: ["admin"] },
      { name: "Staff Management", to: "/staff", icon: UserCog, roles: ["admin"] },
      { name: "Settings", to: "/settings", icon: SettingsIcon, roles: ["admin"] },
    ],
  },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [creditAlerts, setCreditAlerts] = useState([]);
  const [creditAlertOpen, setCreditAlertOpen] = useState(false);
  useEffect(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    api.get("/credits/reminders?days=3").then(({ data }) => {
      if (data.length > 0 && sessionStorage.getItem("credit_alert_shown") !== todayKey) {
        setCreditAlerts(data);
        setCreditAlertOpen(true);
      }
    }).catch(() => {});
  }, []);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(localStorage.getItem("branch_id") || "");
  const [newOrders, setNewOrders] = useState(0);
  const role = user?.role || "agent";

  useEffect(() => {
    api.get("/branches").then(({ data }) => {
      setBranches(data);
      if (!branchId && data.length > 0) {
        setBranchId(data[0].id);
        localStorage.setItem("branch_id", data[0].id);
        localStorage.setItem("branch_name", data[0].name);
      }
    }).catch(() => {});
    const poll = () => api.get("/online-orders/meta/count").then(({ data }) => setNewOrders(data.new || 0)).catch(() => {});
    if (user) {
      poll();
      const t = setInterval(poll, 30000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line
  }, [user?.id]);

  const onBranchChange = (id) => {
    setBranchId(id);
    const b = branches.find((x) => x.id === id);
    localStorage.setItem("branch_id", id);
    localStorage.setItem("branch_name", b?.name || "");
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside
        data-testid="app-sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transform fixed lg:static inset-y-0 left-0 z-40 w-72 pine-gradient text-white flex flex-col transition-transform duration-200`}
      >
        <div className="px-6 py-6 border-b border-emerald-800/50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/15">
              <Pill className="w-6 h-6 text-emerald-100" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-lg leading-tight">Trustmeds</div>
              <div className="text-emerald-200/90 text-xs tracking-widest uppercase">Pharma CRM</div>
            </div>
          </div>
          <div className="mt-4" data-testid="branch-selector-dropdown">
            <Select value={branchId} onValueChange={onBranchChange}>
              <SelectTrigger className="h-9 bg-white/10 border-white/15 text-emerald-50 text-xs">
                <Building2 className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                <SelectValue placeholder="Select branch" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {sections.map((sec) => {
            const items = sec.items.filter((n) => !n.roles || n.roles.includes(role));
            if (items.length === 0) return null;
            return (
              <div key={sec.title} className="mb-3">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-emerald-300/70 font-bold">{sec.title}</div>
                <div className="space-y-0.5">
                  {items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={`nav-${n.to.replace("/", "")}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive ? "bg-white text-emerald-900 shadow-sm" : "text-emerald-50/90 hover:bg-white/10"
                        } ${n.highlight ? "ring-1 ring-emerald-400/30" : ""}`
                      }
                    >
                      <n.icon size={17} />
                      <span className="flex-1">{n.name}</span>
                      {n.badge && newOrders > 0 && (
                        <Badge data-testid="orders-badge" className="bg-red-500 text-white hover:bg-red-500 text-[10px] px-1.5 py-0">{newOrders}</Badge>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-emerald-800/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-900 flex items-center justify-center font-bold">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-emerald-200/80 truncate">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold" data-testid="user-role-badge">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full bg-transparent border-emerald-300/30 text-emerald-50 hover:bg-white hover:text-emerald-900"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-3 left-3 z-50">
        <Button size="icon" variant="secondary" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>

      {creditAlertOpen && creditAlerts.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" data-testid="credit-reminder-popup">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <BellRing className="w-5 h-5 text-amber-700" />
              </span>
              <div>
                <div className="font-display font-bold text-slate-900">Udhaar Payment Reminder</div>
                <div className="text-xs text-slate-500">{creditAlerts.length} customer(s) ki promised pay-date aa gayi ya paas hai</div>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {creditAlerts.map((c) => (
                <div key={c.id} className="py-2.5 flex items-center justify-between gap-2" data-testid={`credit-alert-${c.invoice_no}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{c.customer_name}</div>
                    <div className="text-xs text-slate-500">{c.customer_phone} • {c.invoice_no}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num font-bold text-red-700">₹{Number(c.due_amount || 0).toFixed(0)}</div>
                    <div className={`text-[10px] font-semibold ${c.overdue ? "text-red-600" : "text-amber-600"}`}>{c.overdue ? "OVERDUE" : "Due"}: {c.credit_due_date}</div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => { setCreditAlertOpen(false); sessionStorage.setItem("credit_alert_shown", new Date().toISOString().slice(0, 10)); }}
              data-testid="credit-reminder-close"
              className="mt-4 w-full h-10 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              Samajh Gaya
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
